import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PlusIcon,
  GiftIcon,
  LinkIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ShoppingBagIcon,
  LockClosedIcon,
  CurrencyDollarIcon,
  PhotoIcon,
  PencilIcon,
  ShareIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useFamilyStore } from '../../store/familyStore';

interface Gift {
  id: string;
  gift_list_id: string;
  name: string;
  description: string | null;
  url: string | null;
  image_url: string | null;
  price: number | null;
  priority: string;
  status: 'available' | 'reserved' | 'purchased' | 'received';
  reserved_by_id: string | null;
  reserved_by_name: string | null;
  reserved_at: string | null;
  reservation_occasion: string | null;
  reservation_notes: string | null;
  added_by_id: string | null;
  added_by_name: string | null;
  is_reserved: boolean;
  can_reserve: boolean;
  reservation_visibility: 'hidden' | 'surprise' | 'revealed' | null;
  created_at: string;
}

interface GiftList {
  id: string;
  family_id: string;
  owner_user_id: string | null;
  owner_member_id: string | null;
  name: string;
  description: string | null;
  occasion: string | null;
  occasion_date: string | null;
  owner_name: string;
  gift_count: number;
  reserved_count: number;
  is_own_list: boolean;
  gifts: Gift[];
  is_shared?: boolean;
}

interface AddGiftForm {
  name: string;
  description: string;
  url: string;
  image_url: string;
  price: string;
  priority: string;
}

interface ReserveForm {
  occasion: string;
  notes: string;
  visibility: 'hidden' | 'surprise' | 'revealed';
}

interface EditListForm {
  name: string;
  description: string;
  occasion: string;
}

const occasionLabels: Record<string, string> = {
  christmas: '🎄 Christmas',
  birthday: '🎂 Birthday',
  anniversary: '💍 Anniversary',
  hanukkah: '🕎 Hanukkah',
  nameday: '📛 Name Day',
  valentines: '❤️ Valentine\'s',
  mothers_day: '👩 Mother\'s Day',
  fathers_day: '👨 Father\'s Day',
  easter: '🐰 Easter',
  graduation: '🎓 Graduation',
  wedding: '💒 Wedding',
  other: '🎁 Other',
};

const priorityColors: Record<string, string> = {
  low: 'text-surface-400',
  medium: 'text-amber-400',
  high: 'text-red-400',
};

export default function GiftListDetail() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { currentFamily } = useFamilyStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReserveModal, setShowReserveModal] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<Gift | null>(null);
  const [showEditListModal, setShowEditListModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkedFamilies, setLinkedFamilies] = useState<any[]>([]);
  const [loadingLinkedFamilies, setLoadingLinkedFamilies] = useState(false);

  const { register: registerAdd, handleSubmit: handleSubmitAdd, reset: resetAdd } = useForm<AddGiftForm>({
    defaultValues: { priority: 'medium' }
  });

  const { register: registerReserve, handleSubmit: handleSubmitReserve, reset: resetReserve, watch: watchReserve } = useForm<ReserveForm>({
    defaultValues: { visibility: 'surprise' }
  });

  const { register: registerEdit, handleSubmit: handleSubmitEdit, reset: resetEdit, setValue: setValueEdit } = useForm<AddGiftForm>({
    defaultValues: { priority: 'medium' }
  });

  const { register: registerEditList, handleSubmit: handleSubmitEditList, reset: resetEditList, setValue: setValueEditList } = useForm<EditListForm>({
    defaultValues: { occasion: '' }
  });
  
  const selectedVisibility = watchReserve('visibility');

  // Fetch gift list with gifts
  const { data: list, isLoading } = useQuery({
    queryKey: ['giftList', listId],
    queryFn: async () => {
      const response = await api.get(`/gifts/${listId}`);
      return response.data as GiftList;
    },
    enabled: !!listId,
  });

  // Add gift mutation
  const addMutation = useMutation({
    mutationFn: async (data: AddGiftForm) => {
      const payload = {
        name: data.name,
        description: data.description || null,
        url: data.url || null,
        image_url: data.image_url || null,
        price: data.price ? parseFloat(data.price) : null,
        priority: data.priority,
      };
      const response = await api.post(`/gifts/${listId}/gifts`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftList', listId] });
      toast.success('Gift added!');
      setShowAddModal(false);
      resetAdd();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to add gift');
    },
  });

  // Reserve gift mutation
  const reserveMutation = useMutation({
    mutationFn: async ({ giftId, data }: { giftId: string; data: ReserveForm }) => {
      const response = await api.post(`/gifts/gifts/${giftId}/reserve`, {
        occasion: data.occasion || null,
        notes: data.notes || null,
        visibility: data.visibility,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftList', listId] });
      toast.success('Gift reserved!');
      setShowReserveModal(null);
      resetReserve();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to reserve gift');
    },
  });

  // Unreserve gift mutation
  const unreserveMutation = useMutation({
    mutationFn: async (giftId: string) => {
      const response = await api.post(`/gifts/gifts/${giftId}/unreserve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftList', listId] });
      toast.success('Reservation cancelled');
    },
  });

  // Mark as purchased mutation
  const purchaseMutation = useMutation({
    mutationFn: async (giftId: string) => {
      const response = await api.post(`/gifts/gifts/${giftId}/mark-purchased`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftList', listId] });
      toast.success('Marked as purchased!');
    },
  });

  // Delete gift mutation
  const deleteMutation = useMutation({
    mutationFn: async (giftId: string) => {
      await api.delete(`/gifts/gifts/${giftId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftList', listId] });
      toast.success('Gift removed');
    },
  });

  // Update gift mutation
  const updateMutation = useMutation({
    mutationFn: async ({ giftId, data }: { giftId: string; data: AddGiftForm }) => {
      const payload = {
        name: data.name,
        description: data.description || null,
        url: data.url || null,
        image_url: data.image_url || null,
        price: data.price ? parseFloat(data.price) : null,
        priority: data.priority,
      };
      const response = await api.patch(`/gifts/gifts/${giftId}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftList', listId] });
      toast.success('Gift updated!');
      setShowEditModal(null);
      resetEdit();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update gift');
    },
  });

  const onSubmitAdd = (data: AddGiftForm) => {
    addMutation.mutate(data);
  };

  const onSubmitReserve = (data: ReserveForm) => {
    if (showReserveModal) {
      reserveMutation.mutate({ giftId: showReserveModal, data });
    }
  };

  const handleOpenEdit = (gift: Gift) => {
    setValueEdit('name', gift.name);
    setValueEdit('description', gift.description || '');
    setValueEdit('url', gift.url || '');
    setValueEdit('image_url', gift.image_url || '');
    setValueEdit('price', gift.price?.toString() || '');
    setValueEdit('priority', gift.priority);
    setShowEditModal(gift);
  };

  const onSubmitEdit = (data: AddGiftForm) => {
    if (showEditModal) {
      updateMutation.mutate({ giftId: showEditModal.id, data });
    }
  };

  // Delete list mutation
  const deleteListMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/gifts/${listId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftLists'] });
      toast.success('Wishlist deleted');
      navigate('/gifts');
    },
    onError: () => toast.error('Failed to delete wishlist'),
  });

  // Update list mutation
  const updateListMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; occasion: string }) => {
      const payload: any = {
        name: data.name,
        description: data.description || null,
        occasion: data.occasion || null,
      };
      const response = await api.patch(`/gifts/${listId}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftList', listId] });
      queryClient.invalidateQueries({ queryKey: ['giftLists'] });
      toast.success('Wishlist updated!');
      setShowEditListModal(false);
      resetEditList();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update wishlist');
    },
  });

  const loadLinkedFamilies = async () => {
    if (!currentFamily?.id) return;
    setLoadingLinkedFamilies(true);
    try {
      const response = await api.get(`/family-links/${currentFamily.id}/linked`);
      setLinkedFamilies(response.data || []);
    } catch (error) {
      console.error('Failed to load linked families:', error);
    } finally {
      setLoadingLinkedFamilies(false);
    }
  };

  const handleOpenShareModal = async () => {
    await loadLinkedFamilies();
    setShowShareModal(true);
  };

  const handleShareList = async (targetFamilyId: string) => {
    if (!currentFamily?.id || !listId) return;
    try {
      await api.post(`/family-links/${currentFamily.id}/share`, {
        resource_type: 'gift_list',
        resource_id: listId,
        target_family_id: targetFamilyId,
      });
      toast.success('Wishlist shared successfully!');
      setShowShareModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to share wishlist');
    }
  };

  const handleOpenEditList = () => {
    if (!list) return;
    setValueEditList('name', list.name);
    setValueEditList('description', list.description || '');
    setValueEditList('occasion', list.occasion || '');
    setShowEditListModal(true);
  };

  const onSubmitEditList = (data: { name: string; description: string; occasion: string }) => {
    updateListMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="page-content flex items-center justify-center min-h-[50vh]">
        <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!list) {
    return (
      <div className="page-content">
        <div className="card text-center py-16">
          <h2 className="text-xl font-bold text-white mb-2">List not found</h2>
          <button onClick={() => navigate('/gifts')} className="btn-primary mt-4">
            Back to Lists
          </button>
        </div>
      </div>
    );
  }

  const availableGifts = list.gifts.filter(g => g.status === 'available');
  const reservedGifts = list.gifts.filter(g => g.status !== 'available');

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/gifts')} className="btn-icon">
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{list.name}</h1>
          <p className="text-surface-400 text-sm">
            {list.is_own_list ? 'Your wishlist' : `${list.owner_name}'s wishlist`}
            {list.occasion && ` • ${occasionLabels[list.occasion] || list.occasion}`}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Action buttons - only show for own lists or admins/owners */}
          {list.is_own_list && !list.is_shared && (
            <>
              <button 
                onClick={handleOpenEditList}
                className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-white transition-colors"
                title="Edit wishlist"
              >
                <PencilIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              {currentFamily && (currentFamily.my_role === 'owner' || currentFamily.my_role === 'admin') && (
                <button 
                  onClick={handleOpenShareModal}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-accent-400 transition-colors"
                  title="Share wishlist"
                >
                  <ShareIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Share</span>
                </button>
              )}
              <button 
                onClick={() => {
                  if (confirm('Delete this wishlist?')) {
                    deleteListMutation.mutate();
                  }
                }}
                className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-red-400 transition-colors"
                title="Delete wishlist"
              >
                <TrashIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </>
          )}
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
            <PlusIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Add Gift</span>
          </button>
        </div>
      </div>

      {/* Owner's view notice */}
      {list.is_own_list && (
        <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <div className="flex items-center gap-3">
            <LockClosedIcon className="w-5 h-5 text-amber-400" />
            <p className="text-amber-200 text-sm">
              This is your wishlist. You can see what gifts are reserved, but not by whom!
            </p>
          </div>
        </div>
      )}

      {list.description && (
        <div className="mb-6">
          <p className="text-surface-400">{list.description}</p>
        </div>
      )}

      {/* Gifts */}
      {list.gifts.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-16"
        >
          <GiftIcon className="w-16 h-16 mx-auto text-surface-500 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">No gifts yet</h2>
          <p className="text-surface-400 mb-6">Add some gift ideas to this wishlist</p>
          <button onClick={() => setShowAddModal(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
            <PlusIcon className="w-3.5 h-3.5" />
            Add First Gift
          </button>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {/* Available Gifts */}
          {availableGifts.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-4">
                Available ({availableGifts.length})
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                {availableGifts.map((gift, index) => (
                  <GiftCard
                    key={gift.id}
                    gift={gift}
                    isOwnList={list.is_own_list}
                    currentUserId={user?.id}
                    onReserve={() => setShowReserveModal(gift.id)}
                    onEdit={() => handleOpenEdit(gift)}
                    onDelete={() => {
                      if (confirm('Remove this gift?')) {
                        deleteMutation.mutate(gift.id);
                      }
                    }}
                    index={index}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Reserved/Purchased Gifts */}
          {reservedGifts.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-4">
                Reserved / Purchased ({reservedGifts.length})
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                {reservedGifts.map((gift, index) => (
                  <GiftCard
                    key={gift.id}
                    gift={gift}
                    isOwnList={list.is_own_list}
                    currentUserId={user?.id}
                    onUnreserve={() => unreserveMutation.mutate(gift.id)}
                    onMarkPurchased={() => purchaseMutation.mutate(gift.id)}
                    onEdit={() => handleOpenEdit(gift)}
                    onDelete={() => {
                      if (confirm('Remove this gift?')) {
                        deleteMutation.mutate(gift.id);
                      }
                    }}
                    index={index}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Gift Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="card w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Add Gift</h2>
                <button onClick={() => setShowAddModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitAdd(onSubmitAdd)} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Gift Name *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., Wireless Headphones"
                    {...registerAdd('name', { required: true })}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Description</label>
                  <textarea
                    className="input"
                    rows={2}
                    placeholder="Color, size, specific model..."
                    {...registerAdd('description')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Link (URL)</label>
                  <input
                    type="url"
                    className="input"
                    placeholder="https://..."
                    {...registerAdd('url')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Image URL</label>
                  <input
                    type="url"
                    className="input"
                    placeholder="https://..."
                    {...registerAdd('image_url')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group">
                    <label className="input-label">Price</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="0.00"
                      {...registerAdd('price')}
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Priority</label>
                    <select className="input" {...registerAdd('priority')}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addMutation.isPending}
                    className="btn-primary flex-1"
                  >
                    {addMutation.isPending ? 'Adding...' : 'Add Gift'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reserve Modal */}
      <AnimatePresence>
        {showReserveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowReserveModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="card w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Reserve Gift</h2>
                <button onClick={() => setShowReserveModal(null)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitReserve(onSubmitReserve)} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">For which occasion?</label>
                  <select className="input" {...registerReserve('occasion')}>
                    <option value="">Select occasion...</option>
                    {Object.entries(occasionLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">Notes (private)</label>
                  <textarea
                    className="input"
                    rows={2}
                    placeholder="Reminder notes for yourself..."
                    {...registerReserve('notes')}
                  />
                </div>

                <div className="space-y-3">
                  <label className="input-label">What should the gift owner see?</label>
                  
                  <label className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                    selectedVisibility === 'hidden' 
                      ? 'bg-primary-500/20 border-2 border-primary-500' 
                      : 'bg-surface-800/50 border border-surface-700 hover:border-surface-600'
                  }`}>
                    <input
                      type="radio"
                      value="hidden"
                      className="mt-1 w-4 h-4 text-primary-500 focus:ring-primary-500"
                      {...registerReserve('visibility')}
                    />
                    <div>
                      <span className="text-white font-medium">🎁 Hidden (Complete Surprise)</span>
                      <p className="text-sm text-surface-400 mt-1">
                        Owner won't even know this gift is reserved - it still appears available to them.
                      </p>
                    </div>
                  </label>
                  
                  <label className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                    selectedVisibility === 'surprise' 
                      ? 'bg-primary-500/20 border-2 border-primary-500' 
                      : 'bg-surface-800/50 border border-surface-700 hover:border-surface-600'
                  }`}>
                    <input
                      type="radio"
                      value="surprise"
                      className="mt-1 w-4 h-4 text-primary-500 focus:ring-primary-500"
                      {...registerReserve('visibility')}
                    />
                    <div>
                      <span className="text-white font-medium">🎉 Surprise (Recommended)</span>
                      <p className="text-sm text-surface-400 mt-1">
                        Owner sees "Reserved" but doesn't know who's getting it.
                      </p>
                    </div>
                  </label>
                  
                  <label className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                    selectedVisibility === 'revealed' 
                      ? 'bg-primary-500/20 border-2 border-primary-500' 
                      : 'bg-surface-800/50 border border-surface-700 hover:border-surface-600'
                  }`}>
                    <input
                      type="radio"
                      value="revealed"
                      className="mt-1 w-4 h-4 text-primary-500 focus:ring-primary-500"
                      {...registerReserve('visibility')}
                    />
                    <div>
                      <span className="text-white font-medium">👋 Revealed (No Surprise)</span>
                      <p className="text-sm text-surface-400 mt-1">
                        Owner knows you reserved this gift and when.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowReserveModal(null)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={reserveMutation.isPending}
                    className="btn-primary flex-1"
                  >
                    {reserveMutation.isPending ? 'Reserving...' : 'Reserve'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Gift Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="card w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Edit Gift</h2>
                <button onClick={() => setShowEditModal(null)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitEdit(onSubmitEdit)} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Gift Name *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., Wireless Headphones"
                    {...registerEdit('name', { required: true })}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Description</label>
                  <textarea
                    className="input"
                    rows={2}
                    placeholder="Color, size, specific model..."
                    {...registerEdit('description')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Link (URL)</label>
                  <input
                    type="url"
                    className="input"
                    placeholder="https://..."
                    {...registerEdit('url')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Photo URL</label>
                  <input
                    type="url"
                    className="input"
                    placeholder="https://..."
                    {...registerEdit('image_url')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group">
                    <label className="input-label">Price</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="0.00"
                      {...registerEdit('price')}
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Priority</label>
                    <select className="input" {...registerEdit('priority')}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(null)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="btn-primary flex-1"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit List Modal */}
      <AnimatePresence>
        {showEditListModal && list && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditListModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="card w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Edit Wishlist</h2>
                <button onClick={() => setShowEditListModal(false)} className="btn-icon">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmitEditList(onSubmitEditList)} className="space-y-3">
                <div className="input-group">
                  <label className="input-label text-xs">List Name *</label>
                  <input
                    type="text"
                    className="input text-sm"
                    placeholder="e.g., Birthday Wishlist"
                    {...registerEditList('name', { required: true })}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label text-xs">Description</label>
                  <textarea
                    className="input text-sm"
                    rows={2}
                    placeholder="Optional description..."
                    {...registerEditList('description')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label text-xs">Occasion</label>
                  <select className="input text-sm" {...registerEditList('occasion')}>
                    <option value="">Select occasion...</option>
                    {Object.entries(occasionLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowEditListModal(false)}
                    className="btn-secondary flex-1 text-sm py-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateListMutation.isPending}
                    className="btn-primary flex-1 text-sm py-2"
                  >
                    {updateListMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-surface-800 rounded-xl p-4 w-full max-w-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Share Wishlist</h2>
                <button onClick={() => setShowShareModal(false)} className="btn-icon p-1">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {loadingLinkedFamilies ? (
                <div className="flex justify-center py-6">
                  <div className="w-6 h-6 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                </div>
              ) : linkedFamilies.length === 0 ? (
                <div className="text-center py-6">
                  <UsersIcon className="w-10 h-10 mx-auto mb-2 text-surface-500" />
                  <p className="text-surface-400 text-sm mb-1">No linked families</p>
                  <p className="text-surface-500 text-xs">
                    Link with another family first to share wishlists
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {linkedFamilies.map((family) => (
                    <button
                      key={family.id}
                      onClick={() => handleShareList(family.family_id)}
                      className="w-full p-3 rounded-lg bg-surface-700 hover:bg-surface-600 transition-colors text-left"
                    >
                      <p className="text-white font-medium">{family.family_name}</p>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Gift Card Component
interface GiftCardProps {
  gift: Gift;
  isOwnList: boolean;
  currentUserId?: string;
  onReserve?: () => void;
  onUnreserve?: () => void;
  onMarkPurchased?: () => void;
  onEdit?: () => void;
  onDelete: () => void;
  index: number;
}

function GiftCard({
  gift,
  isOwnList,
  currentUserId,
  onReserve,
  onUnreserve,
  onMarkPurchased,
  onEdit,
  onDelete,
  index,
}: GiftCardProps) {
  const isReservedByMe = gift.reserved_by_id === currentUserId;
  const isPurchased = gift.status === 'purchased';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`card ${gift.is_reserved ? 'opacity-75' : ''}`}
    >
      <div className="flex gap-4">
        {/* Image */}
        {gift.image_url ? (
          <img
            src={gift.image_url}
            alt={gift.name}
            className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-surface-700 flex items-center justify-center flex-shrink-0">
            <PhotoIcon className="w-8 h-8 text-surface-500" />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className={`font-semibold text-white ${gift.is_reserved ? 'line-through' : ''}`}>
                {gift.name}
              </h3>
              {gift.price && (
                <div className="flex items-center gap-1 text-sm text-emerald-400">
                  <CurrencyDollarIcon className="w-4 h-4" />
                  <span>{gift.price.toFixed(2)}</span>
                </div>
              )}
            </div>
            <span className={`text-xs font-medium ${priorityColors[gift.priority]}`}>
              {gift.priority}
            </span>
          </div>

          {gift.description && (
            <p className="text-sm text-surface-400 mt-1 line-clamp-2">{gift.description}</p>
          )}

          {gift.url && (
            <a
              href={gift.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 mt-2"
            >
              <LinkIcon className="w-4 h-4" />
              View Product
            </a>
          )}

          {/* Reservation info */}
          {gift.is_reserved && !isOwnList && (
            <div className="mt-2 text-sm">
              <span className="text-amber-400">
                Reserved by {gift.reserved_by_name}
                {gift.reservation_occasion && ` for ${occasionLabels[gift.reservation_occasion] || gift.reservation_occasion}`}
              </span>
            </div>
          )}

          {gift.is_reserved && isOwnList && (
            <div className="mt-2 text-sm text-amber-400">
              ✓ Someone has reserved this gift
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3">
            {gift.can_reserve && onReserve && (
              <button
                onClick={onReserve}
                className="btn-primary text-xs py-1 px-3"
              >
                <CheckIcon className="w-4 h-4 mr-1" />
                Reserve
              </button>
            )}

            {isReservedByMe && !isPurchased && onUnreserve && (
              <button
                onClick={onUnreserve}
                className="btn-secondary text-xs py-1 px-3"
              >
                Cancel Reservation
              </button>
            )}

            {isReservedByMe && !isPurchased && onMarkPurchased && (
              <button
                onClick={onMarkPurchased}
                className="btn-primary text-xs py-1 px-3"
              >
                <ShoppingBagIcon className="w-4 h-4 mr-1" />
                Purchased
              </button>
            )}

            {isPurchased && isReservedByMe && (
              <span className="text-xs text-emerald-400 flex items-center gap-1">
                <CheckIcon className="w-4 h-4" />
                Purchased
              </span>
            )}

            {(isOwnList || gift.added_by_id === currentUserId) && (
              <>
                {onEdit && (
                  <button
                    onClick={onEdit}
                    className="btn-icon text-surface-500 hover:text-primary-400"
                    title="Edit gift"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={onDelete}
                  className="btn-icon text-surface-500 hover:text-red-400 ml-auto"
                  title="Delete gift"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
